import '@mobiscroll/react/dist/css/mobiscroll.min.css'
import { setOptions } from '@mobiscroll/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useRoster } from '../hooks/useRoster'
import { parseDateOrToday, YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import ResidentRota from './screens/ResidentRota/ResidentRota'
import UserRota from './screens/UserRota/UserRota'
import './common/rotaStyles.css'
import { getNominative } from '@lmc/common/utils'
import { type Carer, type Home, UserRole } from '@lmc/ts-common'
import { Heading, MoreInfoLink, PageHeader, Tabs } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type {
  RotaBuilderFiltersType,
  RotaBuilderQueryFiltersType
} from 'screens/Rostering/types'
import { checkHasFilters } from 'utils'
import { commonPatterns } from 'utils/enhancedTestIds'
import { DAILY, WEEKLY } from '../constants'
import useCountChanges from '../hooks/useCountChanges'
import { getOverlayTypeByErrorCode } from '../utils'
import { AddRotaPatternDropdown } from './common/components'
import {
  CalendarOptions,
  getCalendarOptionsWithEventStack,
  themeOptions
} from './common/constants'
import ShiftRota from './screens/ShiftRota/ShiftRota'

setOptions(themeOptions)

const RotaBuilder = () => {
  const { t } = useTranslation()
  const baseId = 'rota-builder'

  const [active, setActive] = useState('1')

  const sendEvent = useMixpanelEvent()

  const [showAllEvents, setShowAllEvents] = useState(false)
  const [timelineView, setTimelineView] = useState(CalendarOptions.weekly)

  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric<RotaBuilderQueryFiltersType>({
      selectedDate: dayjs().format('YYYY-MM-DD')
    })

  const currentFiltersRef = useRef<RotaBuilderFiltersType>({})

  const filterParams: RotaBuilderFiltersType & { selectedDate?: undefined } =
    useMemo(() => {
      const newFilters: RotaBuilderFiltersType & { selectedDate?: undefined } =
        {
          ...filterParamsGeneric,
          selectedDate: undefined
        }

      delete newFilters.selectedDate

      currentFiltersRef.current = newFilters

      return newFilters
    }, [filterParamsGeneric])

  // `?selectedDate` is read straight off the URL, so a bookmarked, hand-edited or
  // locale-formatted value lands here as an Invalid Date and used to crash the screen
  // from inside useRoster's `toISOString()` (ROST-3612 / CARE-OFFICE-93W). Fall back to
  // today, which is what these screens already show when no date is given at all.
  const selectedDate = useMemo(
    () => parseDateOrToday(filterParamsGeneric.selectedDate).toDate(),
    [filterParamsGeneric.selectedDate]
  )

  const setSelectedDate = (newDate: Date) => {
    const currentFilters = currentFiltersRef.current
    setFilterParams(() => ({
      ...currentFilters,
      // Never write "Invalid Date" back into the URL: dayjs formats an unparseable
      // value to that literal string, which would persist the fault into bookmarks and
      // reloads rather than just affecting this render.
      selectedDate: parseDateOrToday(newDate).format(YEAR_FIRST_DATE_FORMAT)
    }))
  }

  const isByShiftTab = active === '3'

  const combinedFilterParams = {
    ...filterParams,
    ...(isByShiftTab && { siteShift: true })
  }

  const hasFilters = checkHasFilters(combinedFilterParams)

  const onFilterChange = (value: RotaBuilderFiltersType) => {
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...value
    }))
  }

  const flags = useFeatureFlag({
    flagNames: ['rostering_dev_features', 'published_rota']
  })?.data

  const flagsRosteringDevFeaturesEnabled =
    flags?.rostering_dev_features?.enabled

  const flagsPublishedRotaEnabled = flags?.published_rota?.enabled

  const isDailyView =
    timelineView === CalendarOptions.daily ||
    timelineView === CalendarOptions.dailyZoomed

  const { countChangesData, refetchCountChanges } = useCountChanges({
    selectedDate,
    intervalType: isDailyView ? DAILY : WEEKLY,
    filterParams: combinedFilterParams,
    includeWarnings: true
  })

  const {
    users,
    residents,
    usersRevisions,
    residentsRevisions,
    isRotaBuilderLoading,
    invalid,
    shifts,
    metadata,
    errorCode
  } = useRoster({
    selectedDate,
    intervalType:
      timelineView === CalendarOptions.daily ||
      timelineView === CalendarOptions.dailyZoomed
        ? DAILY
        : WEEKLY,
    filterParams: combinedFilterParams
  })

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const isAuditView = userRole === UserRole.auditView
  const residentsLabel = `By ${getNominative(
    preferredPeopleNominative,
    'Resident'
  )}`

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    sendEvent(MixpanelEvents.ROTA_BUILDER_TAB_VIEWED, {
      tabKey: '1',
      tabLabel: t('BY_TEAM_MEMBER'),
      isInitialLoad: true,
      viewType: isDailyView ? 'daily' : 'weekly',
      timelineView: timelineView,
      source: 'rota_builder'
    })
  }, [])

  const overlayType = getOverlayTypeByErrorCode(errorCode ?? undefined)

  const handleToggleShowAllEvents = () => {
    setShowAllEvents((prev) => !prev)

    sendEvent(MixpanelEvents.ROTA_EVENTS_VIEW_TOGGLED, {
      viewType: !showAllEvents ? 'expanded' : 'collapsed',
      tab:
        active === '1'
          ? 'By Team Member'
          : active === '2'
            ? 'By Resident'
            : 'By Shift'
    })
  }

  const calendarOptions = getCalendarOptionsWithEventStack(showAllEvents)

  const handleTabs = (tab: string) => {
    setActive(tab)

    const getTabLabel = (tabKey: string) => {
      switch (tabKey) {
        case '1':
          return t('BY_TEAM_MEMBER')
        case '2':
          return residentsLabel
        case '3':
          return t('BY_SHIFT')
        default:
          return 'Unknown'
      }
    }

    sendEvent(MixpanelEvents.ROTA_BUILDER_TAB_VIEWED, {
      tabKey: tab,
      tabLabel: getTabLabel(tab),
      isInitialLoad: false,
      viewType: isDailyView ? 'daily' : 'weekly',
      timelineView: timelineView,
      source: 'rota_builder'
    })
  }

  return (
    <Container data-testid={baseId}>
      <PageHeader
        data-testid={`${baseId}-header`}
        title={
          <BuilderTitle>
            <TitleHeading level={1} data-testid={`${baseId}-title`}>
              {flagsPublishedRotaEnabled ? t('BUILDER') : t('ROTA_BUILDER')}
            </TitleHeading>
            {flagsRosteringDevFeaturesEnabled && (
              <MoreInfoLink
                helpArticleUrl="todo"
                data-testid={`${baseId}-help-link`}
              />
            )}
          </BuilderTitle>
        }
      >
        {!isAuditView && (
          <AddRotaPatternDropdown testId={`${baseId}-add-pattern`} />
        )}
      </PageHeader>
      <Tabs
        data-testid={`${baseId}-tabs`}
        onChange={handleTabs}
        activeKey={active}
        tabBarStyle={tabBarStyle}
        items={[
          {
            key: '1',
            label: t('BY_TEAM_MEMBER'),
            children: (
              <UserRota
                testId={commonPatterns.tab(baseId, 'team-member')}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota={false}
                invalid={invalid}
                resources={users}
                events={usersRevisions}
                isLoading={isRotaBuilderLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                metadata={metadata}
                overlayType={overlayType}
                calendarOptions={calendarOptions}
                showAllEvents={showAllEvents}
                onToggleShowAllEvents={handleToggleShowAllEvents}
              />
            )
          },
          {
            key: '2',
            label: residentsLabel,
            children: (
              <ResidentRota
                testId={commonPatterns.tab(baseId, 'resident')}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota={false}
                resources={residents}
                events={residentsRevisions}
                isLoading={isRotaBuilderLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                metadata={metadata}
                overlayType={overlayType}
                calendarOptions={calendarOptions}
                showAllEvents={showAllEvents}
                onToggleShowAllEvents={handleToggleShowAllEvents}
              />
            )
          },
          {
            key: '3',
            label: t('BY_SHIFT'),
            children: (
              <ShiftRota
                testId={commonPatterns.tab(baseId, 'shift')}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota={false}
                resources={shifts}
                events={residentsRevisions?.map((item) => ({
                  ...item,
                  resource: item.instanceType || ''
                }))}
                isLoading={isRotaBuilderLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                calendarOptions={calendarOptions}
                showAllEvents={showAllEvents}
                onToggleShowAllEvents={handleToggleShowAllEvents}
              />
            )
          }
        ]}
      />
    </Container>
  )
}

const tabBarStyle = { paddingLeft: 24 }

const Container = styled.div`
  --rota-container-vertical-padding: 24px;

  padding: var(--rota-container-vertical-padding, 24px) 48px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BuilderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

export default RotaBuilder
